import React, {useCallback, useEffect, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import PropTypes from 'prop-types';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';
import {generateUUID} from '../../../../../utility/uuidUtil';

const MAX_COLUMNS = 5;

const QuestionGrid = ({
                          index,
                          componentId,
                          value,
                          onChange,
                          descriptionHtml,
                          onDescriptionHtmlChange,
                          commitDescriptionHtml,
                          isEditMode,
                          isFormArchived,
                          readOnly,
                          placeholderValues,
                          accessLevel,
                          inputs,
                          columns,
                          error,
                          submitMode,
                          audience,
                          recipientType,
                          linkedToStudents,
                          allowCustomRows
                      }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const stopEventPropagation = useCallback((event) => {
        event.stopPropagation();
    }, []);

    const customRows = Array.isArray(value?.__customRows) ? value.__customRows : [];
    const allInputs = [...(inputs || []), ...customRows.map((row) => ({...row, required: false, isCustom: true}))];

    const updateGridValue = (updater) => {
        const nextValue = updater({...((value && typeof value === 'object') ? value : {})});
        onChange({
            id: componentId,
            value: nextValue
        });
    };

    const addCustomRow = () => {
        updateGridValue((current) => ({
            ...current,
            __customRows: [
                ...(Array.isArray(current.__customRows) ? current.__customRows : []),
                {id: generateUUID(), title: ''}
            ]
        }));
    };

    const updateCustomRowTitle = (rowId, title) => {
        updateGridValue((current) => ({
            ...current,
            __customRows: (Array.isArray(current.__customRows) ? current.__customRows : [])
                .map((row) => row.id === rowId ? {...row, title} : row)
        }));
    };

    const removeCustomRow = (rowId) => {
        updateGridValue((current) => {
            const next = {
                ...current,
                __customRows: (Array.isArray(current.__customRows) ? current.__customRows : [])
                    .filter((row) => row.id !== rowId)
            };
            delete next[rowId];
            return next;
        });
    };

    const isCustomRow = (inputId) => customRows.some((row) => row.id === inputId);

    const renderRowLabel = (input, inputIndex, mobile = false) => {
        if (!isCustomRow(input.id)) {
            return (
                <Typography
                    variant="body1"
                    sx={{
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        whiteSpace: 'normal',
                        lineHeight: 1.5,
                        textAlign: mobile
                            ? 'center'
                            : {
                                xs: 'center',
                                sm: 'left'
                            }
                    }}
                >
                    {typeof input.title === 'string' ? input.title : ''}
                    {Boolean(input.required) && (
                        <Typography component="span" color="error" sx={{ml: 1}}>
                            *
                        </Typography>
                    )}
                </Typography>
            );
        }

        return (
            <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{width: '100%', minWidth: 0}}
            >
                <TextField
                    fullWidth
                    variant="outlined"
                    value={input.title || ''}
                    onChange={(e) => updateCustomRowTitle(input.id, e.target.value)}
                    placeholder={`Row ${inputIndex + 1}`}
                    disabled={readOnly || !submitMode}
                    multiline
                    minRows={1}
                    maxRows={16}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        '& .MuiOutlinedInput-root': {
                            minHeight: '40px'
                        }
                    }}
                />
                {!readOnly && submitMode && (
                    <IconButton onClick={() => removeCustomRow(input.id)}>
                        <DeleteIcon/>
                    </IconButton>
                )}
            </Box>
        );
    };

    const handleInputTitleChange = (inputIndex, newTitle) => {
        onChange({
            index,
            option: {title: newTitle},
            optionId: inputIndex,
            listKey: 'inputs'
        });
    };

    const handleInputRequiredChange = (inputIndex, isRequired) => {
        onChange({
            index,
            option: {required: isRequired},
            optionId: inputIndex,
            listKey: 'inputs'
        });
    };

    const addInput = () => {
        const newInput = {
            id: generateUUID(),
            title: '',
            required: false
        };
        onChange({index, isAdd: true, option: newInput, listKey: 'inputs'});
    };

    const removeInput = (inputIndex) => {
        if (inputs.length > 1) {
            onChange({
                index,
                isRemove: true,
                optionId: inputIndex,
                listKey: 'inputs'
            });
        }
    };

    const handleAnswerChange = (inputId, answer, columnId = null) => {
        const isMultiColumn = columns && columns.length > 0;

        if (isMultiColumn && columnId) {
            updateGridValue((current) => {
                const inputValue = current[inputId] || {};
                const updatedInputValue = {
                    ...inputValue,
                    [columnId]: answer
                };
                return {
                    ...current,
                    [inputId]: updatedInputValue
                };
            });
        } else {
            updateGridValue((current) => ({
                ...current,
                [inputId]: answer
            }));
        }
    };

    const handleColumnTitleChange = (columnIndex, newTitle) => {
        onChange({
            index,
            option: {title: newTitle},
            optionId: columnIndex,
            listKey: 'columns'
        });
    };

    const addColumn = () => {
        if (columns && columns.length >= MAX_COLUMNS) {
            return;
        }
        const newColumn = {
            id: generateUUID(),
            title: ''
        };
        onChange({index, isAdd: true, option: newColumn, listKey: 'columns'});
    };

    const removeColumn = (columnIndex) => {
        if (columns && columns.length > 1) {
            onChange({
                index,
                isRemove: true,
                optionId: columnIndex,
                listKey: 'columns'
            });
        }
    };

    const buildEditableColumns = () => {
        if (!columns || columns.length === 0) return null;

        return (
            <Box width="100%" mt={2}>
                {columns.map((column, columnIndex) => (
                    <Box
                        key={String(column.id || columnIndex)}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                        mb={2}
                    >
                        <Box display="flex" alignItems="center" gap={1} width="90%">
                            <Typography>{columnIndex + 1}.</Typography>
                        <TextField
                            variant="standard"
                            value={typeof column.title === 'string' ? column.title : ''}
                            fullWidth
                            multiline
                            minRows={1}
                            maxRows={16}
                            placeholder="Enter column title"
                            onChange={e => handleColumnTitleChange(columnIndex, e.target.value)}
                            disabled={isFormArchived}
                            onMouseDown={stopEventPropagation}
                            onClick={stopEventPropagation}
                            onDoubleClick={stopEventPropagation}
                            onPointerDown={stopEventPropagation}
                            onFocus={stopEventPropagation}
                        />
                        </Box>
                        {columns.length > 1 && (
                            <IconButton
                                size="small"
                                onClick={() => removeColumn(columnIndex)}
                                disabled={isFormArchived}
                                onMouseDown={stopEventPropagation}
                                onDoubleClick={stopEventPropagation}
                            >
                                <DeleteIcon/>
                            </IconButton>
                        )}
                    </Box>
                ))}
                <Button
                    onClick={addColumn}
                    startIcon={<AddIcon/>}
                    disabled={isFormArchived || (columns && columns.length >= MAX_COLUMNS)}
                    onMouseDown={stopEventPropagation}
                    onDoubleClick={stopEventPropagation}
                    sx={{mt: 1}}
                >
                    Add Column
                </Button>
            </Box>
        );
    };

    const buildEditableInputs = () => (
        <>
            {(inputs || []).map((input, inputIndex) => (
                <Box
                    key={String(input.id || inputIndex)}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    mb={2}
                >
                    <Box display="flex" alignItems="center" gap={1} width="90%">
                        <Typography>{inputIndex + 1}.</Typography>
                        <TextField
                            variant="standard"
                            value={typeof input.title === 'string' ? input.title : ''}
                            fullWidth
                            multiline
                            minRows={1}
                            maxRows={16}
                            placeholder="Enter question text"
                            onChange={e => handleInputTitleChange(inputIndex, e.target.value)}
                            disabled={isFormArchived}
                            onMouseDown={stopEventPropagation}
                            onClick={stopEventPropagation}
                            onDoubleClick={stopEventPropagation}
                            onPointerDown={stopEventPropagation}
                            onFocus={stopEventPropagation}
                        />
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={Boolean(input.required)}
                                    onChange={e => handleInputRequiredChange(inputIndex, e.target.checked)}
                                    disabled={isFormArchived}
                                    onMouseDown={stopEventPropagation}
                                    onClick={stopEventPropagation}
                                    onDoubleClick={stopEventPropagation}
                                    onPointerDown={stopEventPropagation}
                                    onFocus={stopEventPropagation}
                                />
                            )}
                            label="Required"
                            onMouseDown={stopEventPropagation}
                            onDoubleClick={stopEventPropagation}
                        />
                    </Box>
                    {inputs.length > 1 && (
                        <IconButton
                            size="small"
                            onClick={() => removeInput(inputIndex)}
                            disabled={isFormArchived}
                            onMouseDown={stopEventPropagation}
                            onDoubleClick={stopEventPropagation}
                        >
                            <DeleteIcon/>
                        </IconButton>
                    )}
                </Box>
            ))}
            <Button
                onClick={addInput}
                startIcon={<AddIcon/>}
                disabled={isFormArchived}
                onMouseDown={stopEventPropagation}
                onDoubleClick={stopEventPropagation}
                sx={{mt: 1}}
            >
                Add Question
            </Button>
        </>
    );

    return (
        <Box
            sx={{
                mt: 2,
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflowX: 'visible',
                overflowY: 'visible'
            }}
        >
            {isEditMode
                ? (
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            overflowX: 'auto'
                        }}
                    >
                        <RichEditor
                            currentDescription={draftDescriptionHtml}
                            setCurrentDescription={handleDraftChange}
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            placeholderValues={placeholderValues}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />
                        <Grid container spacing={2} mt={1}>
                            <Grid item xs={12} md={6}>
                                <Typography fontWeight="bold" mb={2}>Questions</Typography>
                                {buildEditableInputs()}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography fontWeight="bold" mb={2}>Columns (Optional)</Typography>
                                {buildEditableColumns()}
                                {(!columns || columns.length === 0) && (
                                    <Button
                                        onClick={addColumn}
                                        startIcon={<AddIcon/>}
                                        disabled={isFormArchived || (columns && columns.length >= MAX_COLUMNS)}
                                        sx={{mt: 1}}
                                        variant="outlined"
                                    >
                                        Add Column
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                        <Box mt={1}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={Boolean(allowCustomRows)}
                                        onChange={(e) =>
                                            onChange({
                                                index,
                                                allowCustomRows: e.target.checked
                                            })}
                                        disabled={isFormArchived}
                                    />
                                }
                                label="Allow custom rows while submitting"
                            />
                        </Box>
                    </Box>
                )
                : (
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            overflowX: 'visible'
                        }}
                    >
                        <RichEditorResultText
                            id={componentId}
                            description={descriptionHtml}
                            placeholderValues={placeholderValues}
                            readOnly={readOnly}
                            error={!!error}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            submitMode={submitMode}
                        />
                        <FormControl
                            variant="standard"
                            error={!!error}
                            fullWidth
                            sx={{
                                width: '100%',
                                maxWidth: '100%',
                                minWidth: 0
                            }}
                        >
                            <Box
                                sx={{
                                    mt: 2,
                                    display: {
                                        xs: 'block',
                                        sm: 'none'
                                    }
                                }}
                            >
                                {allInputs.map((input, inputIndex) => {
                                    const isMultiColumn = columns && columns.length > 0;
                                    const inputValue = value?.[input.id];

                                    return (
                                        <Paper key={String(input.id || inputIndex)} variant="outlined" sx={{
                                            p: 2,
                                            mb: 1.5
                                        }}>
                                            <Box
                                                sx={{
                                                    mb: 1
                                                }}
                                            >
                                                {renderRowLabel(input, inputIndex, true)}
                                            </Box>

                                            {isMultiColumn
                                                ? columns.map((column, columnIndex) => {
                                                    const columnValue =
                                                        typeof inputValue === 'object' && inputValue !== null
                                                            ? (typeof inputValue[column.id] === 'string'
                                                                ? inputValue[column.id]
                                                                : '')
                                                            : '';
                                                    const columnError =
                                                        typeof error === 'object' &&
                                                        error !== null &&
                                                        error[input.id]
                                                            ? (typeof error[input.id][column.id] === 'string'
                                                                ? error[input.id][column.id]
                                                                : '')
                                                            : '';

                                                    return (
                                                        <Box key={String(column.id || columnIndex)} mb={1.25}>
                                                            <Typography variant="body1" sx={{
                                                                display: 'block',
                                                                mb: 0.5,
                                                                width: '100%',
                                                                maxWidth: '100%',
                                                                fontWeight: 'bold',
                                                                whiteSpace: 'normal',
                                                                wordBreak: 'break-word',
                                                                overflowWrap: 'anywhere'
                                                            }}>
                                                                {typeof column.title === 'string'
                                                                    ? column.title
                                                                    : `Column ${columnIndex + 1}`}
                                                            </Typography>
                                                            <TextField
                                                                fullWidth
                                                                variant="outlined"
                                                                value={columnValue}
                                                                onChange={e => handleAnswerChange(
                                                                    input.id,
                                                                    e.target.value,
                                                                    column.id
                                                                )}
                                                                disabled={readOnly || !submitMode}
                                                                required={input.required}
                                                                placeholder={submitMode
                                                                    ? 'Enter your answer'
                                                                    : 'Answer will appear here'}
                                                                multiline
                                                                minRows={1}
                                                                maxRows={16}
                                                                error={!!columnError}
                                                                sx={{
                                                                    maxWidth: {
                                                                        xs: '100%',
                                                                        sm: '250px'
                                                                    },
                                                                    '& .MuiOutlinedInput-root': {
                                                                        minHeight: '40px'
                                                                    }
                                                                }}
                                                            />
                                                            {columnError && (
                                                                <FormHelperText error sx={{mt: 0.5}}>
                                                                    {columnError}
                                                                </FormHelperText>
                                                            )}
                                                        </Box>
                                                    );
                                                })
                                                : (
                                                    <>
                                                        <TextField
                                                            fullWidth
                                                            variant="outlined"
                                                            value={typeof inputValue === 'string'
                                                                ? inputValue
                                                                : ''}
                                                            onChange={e => handleAnswerChange(
                                                                input.id,
                                                                e.target.value
                                                            )}
                                                            disabled={readOnly || !submitMode}
                                                            required={input.required}
                                                            placeholder={submitMode
                                                                ? 'Enter your answer'
                                                                : 'Answer will appear here'}
                                                            multiline
                                                            minRows={1}
                                                            maxRows={16}
                                                            error={typeof error === 'object' && error !== null && error[input.id] ? true : false}
                                                            sx={{
                                                                maxWidth: {
                                                                    xs: '100%',
                                                                    sm: '250px'
                                                                },
                                                                '& .MuiOutlinedInput-root': {
                                                                    minHeight: '40px'
                                                                }
                                                            }}
                                                        />
                                                        {typeof error === 'object' &&
                                                            error !== null &&
                                                            error[input.id] && (
                                                                <FormHelperText error sx={{mt: 0.5}}>
                                                                    {typeof error[input.id] === 'string'
                                                                        ? error[input.id]
                                                                        : ''}
                                                                </FormHelperText>
                                                            )}
                                                    </>
                                                )}
                                        </Paper>
                                    );
                                })}
                            </Box>
                            <Box
                                sx={{
                                    mt: 2,
                                    width: '100%',
                                    maxWidth: '100%',
                                    display: {
                                        xs: 'none',
                                        sm: 'block'
                                    }
                                }}
                            >
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        width: '100%',
                                        maxWidth: '100%',
                                        borderColor: 'divider'
                                    }}
                                >
                                <TableContainer
                                    sx={{
                                        maxWidth: '100%',
                                        overflowX: 'auto',
                                        '&::-webkit-scrollbar': {height: '4px'},
                                        '&::-webkit-scrollbar-track': {backgroundColor: '#f1f1f1'},
                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: '#c1c1c1',
                                            borderRadius: '4px',
                                        },
                                        '&::-webkit-scrollbar-thumb:hover': {backgroundColor: '#a1a1a1'},
                                        scrollbarWidth: 'thin',
                                        scrollbarColor: '#c1c1c1 #f1f1f1',
                                    }}
                                >
                                    <Table
                                        sx={{
                                            width: '100%',
                                            tableLayout: {
                                                xs: 'auto',
                                                sm: 'fixed'
                                            }
                                        }}
                                    >
                                    {columns && columns.length > 0 && (
                                        <TableHead>
                                            <TableRow>
                                                <TableCell
                                                    sx={{
                                                        ...theme.typography.body1,
                                                        color: theme.palette.text.primary,
                                                        fontWeight: 'bold',
                                                        width: {
                                                            sm: '200px'
                                                        },
                                                        minWidth: {
                                                            sm: '200px'
                                                        }
                                                    }}
                                                />
                                                {columns.map((column, columnIndex) => (
                                                    <TableCell
                                                        key={String(column.id || columnIndex)}
                                                        align="center"
                                                        sx={{
                                                            ...theme.typography.body1,
                                                            color: theme.palette.text.primary,
                                                            fontWeight: 'bold',
                                                            minWidth: {
                                                                sm: '120px'
                                                            },
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'normal',
                                                            wordBreak: 'break-word',
                                                            overflowWrap: 'anywhere',
                                                            boxSizing: 'border-box'
                                                        }}
                                                    >
                                                        {typeof column.title === 'string'
                                                            ? column.title
                                                            : `Column ${columnIndex + 1}`}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                    )}
                                    <TableBody>
                                        {allInputs.map((input, inputIndex) => {
                                            const isMultiColumn = columns && columns.length > 0;
                                            const inputValue = value?.[input.id];

                                            return (
                                                <TableRow
                                                    key={String(input.id || inputIndex)}
                                                    sx={{
                                                        display: {
                                                            xs: 'block',
                                                            sm: 'table-row'
                                                        },
                                                        mb: {
                                                            xs: 1.5,
                                                            sm: 0
                                                        },
                                                        borderBottom: {
                                                            xs: 2,
                                                            sm: 1
                                                        },
                                                        borderColor: 'divider',
                                                        '&:last-child': {
                                                            mb: 0,
                                                            borderBottom: 0
                                                        }
                                                    }}
                                                >
                                                    <TableCell
                                                        component="th"
                                                        scope="row"
                                                        sx={{
                                                            ...theme.typography.body1,
                                                            color: theme.palette.text.primary,
                                                            width: {
                                                                xs: '100%',
                                                                sm: '200px'
                                                            },
                                                            minWidth: {
                                                                xs: '100%',
                                                                sm: '200px'
                                                            },
                                                            maxWidth: {
                                                                sm: '200px'
                                                            },
                                                            verticalAlign: 'top',
                                                            paddingTop: 2,
                                                            paddingBottom: 2,
                                                            display: {
                                                                xs: 'block',
                                                                sm: 'table-cell'
                                                            }
                                                        }}
                                                    >
                                                        {renderRowLabel(input, inputIndex)}
                                                    </TableCell>
                                                    {isMultiColumn
                                                        ? (
                                                            columns.map((column, columnIndex) => {
                                                                const columnValue =
                                                                    typeof inputValue === 'object' && inputValue !== null
                                                                        ? (typeof inputValue[column.id] === 'string'
                                                                            ? inputValue[column.id]
                                                                            : '')
                                                                        : '';
                                                                const columnError =
                                                                    typeof error === 'object' &&
                                                                    error !== null &&
                                                                    error[input.id]
                                                                        ? (typeof error[input.id][column.id] === 'string'
                                                                            ? error[input.id][column.id]
                                                                            : '')
                                                                        : '';

                                                                return (
                                                                    <TableCell
                                                                        key={String(column.id || columnIndex)}
                                                                        sx={{
                                                                            width: {
                                                                                xs: '100%'
                                                                            },
                                                                            verticalAlign: 'top',
                                                                            paddingTop: 2,
                                                                            paddingBottom: 2,
                                                                            display: {
                                                                                xs: 'block',
                                                                                sm: 'table-cell'
                                                                            }
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="body1"
                                                                            sx={{
                                                                                display: {
                                                                                    xs: 'block',
                                                                                    sm: 'none'
                                                                                },
                                                                                mb: 0.5,
                                                                                width: '100%',
                                                                                maxWidth: '100%',
                                                                                fontWeight: 'bold',
                                                                                whiteSpace: 'normal',
                                                                                wordBreak: 'break-word',
                                                                                overflowWrap: 'anywhere'
                                                                            }}
                                                                        >
                                                                            {typeof column.title === 'string'
                                                                                ? column.title
                                                                                : `Column ${columnIndex + 1}`}
                                                                        </Typography>
                                                                        <TextField
                                                                            fullWidth
                                                                            variant="outlined"
                                                                            value={columnValue}
                                                                            onChange={e => handleAnswerChange(
                                                                                input.id,
                                                                                e.target.value,
                                                                                column.id
                                                                            )}
                                                                            disabled={readOnly || !submitMode}
                                                                            required={input.required}
                                                                            placeholder={submitMode
                                                                                ? 'Enter your answer'
                                                                                : 'Answer will appear here'}
                                                                            multiline
                                                                            minRows={1}
                                                                            maxRows={16}
                                                                            error={!!columnError}
                                                                            sx={{
                                                                                width: '100%',
                                                                                minWidth: 0,
                                                                                '& .MuiOutlinedInput-root': {
                                                                                    minHeight: '40px'
                                                                                }
                                                                            }}
                                                                        />
                                                                        {columnError && (
                                                                            <FormHelperText
                                                                                error
                                                                                sx={{mt: 0.5}}
                                                                            >
                                                                                {columnError}
                                                                            </FormHelperText>
                                                                        )}
                                                                    </TableCell>
                                                                );
                                                            })
                                                        )
                                                        : (
                                                            <TableCell
                                                                sx={{
                                                                    width: {
                                                                        xs: '100%'
                                                                    },
                                                                    verticalAlign: 'top',
                                                                    paddingTop: 2,
                                                                    paddingBottom: 2,
                                                                    display: {
                                                                        xs: 'block',
                                                                        sm: 'table-cell'
                                                                    }
                                                                }}
                                                            >
                                                                <TextField
                                                                    fullWidth
                                                                    variant="outlined"
                                                                    value={typeof inputValue === 'string'
                                                                        ? inputValue
                                                                        : ''}
                                                                    onChange={e => handleAnswerChange(
                                                                        input.id,
                                                                        e.target.value
                                                                    )}
                                                                    disabled={readOnly || !submitMode}
                                                                    required={input.required}
                                                                    placeholder={submitMode
                                                                        ? 'Enter your answer'
                                                                        : 'Answer will appear here'}
                                                                    multiline
                                                                    minRows={1}
                                                                    maxRows={16}
                                                                    error={typeof error === 'object' && error !== null && error[input.id] ? true : false}
                                                                    sx={{
                                                                        width: '100%',
                                                                        minWidth: 0,
                                                                        '& .MuiOutlinedInput-root': {
                                                                            minHeight: '40px'
                                                                        }
                                                                    }}
                                                                />
                                                                {typeof error === 'object' &&
                                                                    error !== null &&
                                                                    error[input.id] && (
                                                                        <FormHelperText
                                                                            error
                                                                            sx={{mt: 0.5}}
                                                                        >
                                                                            {typeof error[input.id] === 'string'
                                                                                ? error[input.id]
                                                                                : ''}
                                                                        </FormHelperText>
                                                                    )}
                                                            </TableCell>
                                                        )}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                    </Table>
                                </TableContainer>
                                </Paper>
                            </Box>
                            {Boolean(allowCustomRows) && submitMode && !readOnly && (
                                <Button
                                    onClick={addCustomRow}
                                    startIcon={<AddIcon/>}
                                    sx={{mt: 2, alignSelf: 'flex-start'}}
                                >
                                    Add row
                                </Button>
                            )}
                            {typeof error === 'string' && (
                                <FormHelperText>{error}</FormHelperText>
                            )}
                        </FormControl>
                    </Box>
                )}
        </Box>
    );
};

QuestionGrid.propTypes = {
    componentId: PropTypes.string,
    value: PropTypes.object,
    inputs: PropTypes.array,
    columns: PropTypes.array,
    index: PropTypes.number,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    submitMode: PropTypes.bool,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    allowCustomRows: PropTypes.bool
};

export default QuestionGrid;
